import { type OnboardingChatResults } from '@fruitful/types';
import { useCallback } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';
import { captureException } from '../../logger/Logger';

interface UpsertMastraThreadBody {
    mastraThreadId: string;
    membershipId: string | null;
    userId: string | null;
    visitorId: string | null;
    agentId: string;
    version: string;
    results: OnboardingChatResults | null;
}

interface UpsertMastraThreadURLParams {
    mastraThreadId: string;
}

export interface UpsertMastraThreadParams {
    mastraThreadId: string;
    agentId: string;
    membershipId?: string | null;
    userId?: string | null;
    visitorId?: string | null;
    version: string;
    results?: OnboardingChatResults;
}

const mutateUpsertMastraThread = requestBuilder.mutation<
    unknown,
    UpsertMastraThreadBody,
    UpsertMastraThreadURLParams
>({
    endpoint: ({ mastraThreadId }) =>
        `/api/mastra-threads/${encodeURIComponent(mastraThreadId)}`,
    method: 'PUT',
});

export const useUpsertMastraThread = () => {
    const { mutate, isPending } = mutateUpsertMastraThread({
        onError: (error) => {
            captureException(error);
        },
    });

    const upsertMastraThread = useCallback(
        async ({
            mastraThreadId,
            agentId,
            membershipId,
            userId,
            visitorId,
            version,
            results,
        }: UpsertMastraThreadParams): Promise<void> => {
            await new Promise<void>((resolve, reject) => {
                mutate(
                    {
                        urlParams: { mastraThreadId },
                        body: {
                            mastraThreadId,
                            membershipId: membershipId ?? null,
                            userId: userId ?? null,
                            visitorId: visitorId ?? null,
                            agentId,
                            version,
                            results: results ?? null,
                        },
                    },
                    {
                        onSuccess: () => {
                            resolve();
                        },
                        onError: (error) => {
                            reject(error);
                        },
                    },
                );
            });
        },
        [mutate],
    );

    return {
        upsertMastraThread,
        isUpsertingMastraThread: isPending,
    };
};
